/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { ReactNode } from 'react';
import { styled } from '@superset-ui/core';

/** 包裹 Modal 内 antd Form，统一标签/辅助文字字号与间距 */
export const ModalFormShell = styled.div`
  .ant-form-item {
    margin-bottom: ${({ theme }) => theme.gridUnit * 4}px;
  }

  /* 与 assets/stylesheets/antd/index.less vertical Form 标签一致 */
  .modal-field-label {
    font-size: 13px;
    font-weight: ${({ theme }) => theme.typography.weights.normal};
    color: ${({ theme }) => theme.colors.primary.light1};
    line-height: 1.5;
    height: auto;
    text-transform: none;
  }

  .ant-form-item-explain,
  .ant-form-item-extra {
    font-size: ${({ theme }) => theme.typography.sizes.s}px;
    line-height: 1.5;
  }

  .ant-card-head-title {
    font-size: ${({ theme }) => theme.typography.sizes.m}px;
    font-weight: ${({ theme }) => theme.typography.weights.bold};
    color: ${({ theme }) => theme.colors.grayscale.dark1};
  }

  .ant-tabs-tab {
    font-size: ${({ theme }) => theme.typography.sizes.m}px;
  }
`;

/** 与 antd vertical Form 标签一致（非 Form.Item 场景，如 TagModal） */
export const ModalFieldLabel = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.gridUnit}px;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.typography.weights.normal};
  color: ${({ theme }) => theme.colors.primary.light1};
  line-height: 1.5;
  height: auto;
  text-transform: none;
`;

/** 顶部说明条（原各页 PromptBox） */
export const ModalHintBox = styled.div`
  margin-bottom: ${({ theme }) => theme.gridUnit * 3}px;
  padding: ${({ theme }) => theme.gridUnit * 2}px
    ${({ theme }) => theme.gridUnit * 3}px;
  border-radius: ${({ theme }) => theme.borderRadius}px;
  border: 1px solid ${({ theme }) => theme.colors.primary.light4};
  background: ${({ theme }) => theme.colors.primary.light5};
  color: ${({ theme }) => theme.colors.grayscale.dark1};
`;

export const ModalHintTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.sizes.m}px;
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grayscale.dark1};
`;

export const ModalHintText = styled.div`
  margin-top: ${({ theme }) => theme.gridUnit}px;
  font-size: ${({ theme }) => theme.typography.sizes.s}px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.grayscale.base};
`;

/** 说明条内的条目列表 */
export const ModalHintList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.gridUnit * 1.5}px;
  margin-top: ${({ theme }) => theme.gridUnit}px;
`;

export const ModalHintListItem = styled.div<{
  $tone?: 'default' | 'success' | 'warning' | 'error';
}>`
  font-size: ${({ theme }) => theme.typography.sizes.s}px;
  line-height: 1.6;
  color: ${({ theme, $tone }) => {
    if ($tone === 'success') {
      return theme.colors.success.dark1;
    }
    if ($tone === 'warning') {
      return theme.colors.warning.dark1;
    }
    if ($tone === 'error') {
      return theme.colors.error.dark1;
    }
    return theme.colors.grayscale.base;
  }};
`;

/** Card 区块标题（与 Modal 内 ant-card 标题一致） */
export const ModalCardTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.sizes.m}px;
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.grayscale.dark1};
  line-height: 1.4;
`;

/** Tabs 面板内容区内边距 */
export const ModalTabContent = styled.div`
  padding: ${({ theme }) => theme.gridUnit * 4}px 0;
`;

/** 次要标签 / 摘要项标签 */
export const ModalCaption = styled.div`
  font-size: ${({ theme }) => theme.typography.sizes.s}px;
  color: ${({ theme }) => theme.colors.grayscale.base};
  line-height: 1.4;
`;

/** 摘要主值 */
export const ModalEmphasis = styled.div`
  font-size: ${({ theme }) => theme.typography.sizes.m}px;
  font-weight: ${({ theme }) => theme.typography.weights.medium};
  color: ${({ theme }) => theme.colors.grayscale.dark1};
  line-height: 1.5;
`;

export function renderModalCardTitle(title: string): ReactNode {
  return <ModalCardTitle>{title}</ModalCardTitle>;
}
